import { useState, useEffect } from 'react'
import api from '../../api/axiosInstance.js'
import {
  Box,
  Container,
  Typography,
  Card,
  Grid,
  TextField,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Skeleton,
  alpha,
  Stack,
  InputAdornment,
  Chip,
  LinearProgress,
} from '@mui/material'
import {
  Add,
  Search,
  MoreVert,
  CheckCircle,
  Error,
  Warning,
  Pause,
  PlayArrow,
  Delete,
  Visibility,
  Edit,
  Refresh,
  TrendingUp,
  TrendingDown,
  Circle,
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

// Status Component
const StatusIndicator = ({ status }) => {
  const statusConfig = {
    up: { 
      color: '#10b981', 
      bg: alpha('#10b981', 0.15),
      label: 'Aktif',
      icon: CheckCircle
    },
    down: { 
      color: '#ef4444', 
      bg: alpha('#ef4444', 0.15),
      label: 'Hata',
      icon: Error
    },
    uncertain: { 
      color: '#f59e0b', 
      bg: alpha('#f59e0b', 0.15),
      label: 'Belirsiz',
      icon: Warning
    },
    maintanance: { 
      color: '#6366f1', 
      bg: alpha('#6366f1', 0.15),
      label: 'Bakım',
      icon: Pause
    },
    paused: { 
      color: '#64748b', 
      bg: alpha('#64748b', 0.15),
      label: 'Durduruldu',
      icon: Pause
    },
  }

  const config = statusConfig[status] || statusConfig.uncertain
  const Icon = config.icon

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        bgcolor: config.bg,
        borderRadius: 1,
        border: `1px solid ${alpha(config.color, 0.3)}`,
      }}
    >
      <Icon sx={{ fontSize: 14, color: config.color }} />
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: config.color,
        }}
      >
        {config.label}
      </Typography>
    </Box>
  )
}

// Monitor Card
const MonitorCard = ({ monitor, onView, onEdit, onDelete, onToggle }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const navigate = useNavigate()

  const getHost = () => {
    switch (monitor.monitorType) {
      case 'HttpMonitor':
        return monitor.httpMonitor?.host
      case 'PingMonitor':
        return monitor.pingMonitor?.host
      case 'PortMonitor':
        return monitor.portMonitor?.host
      case 'KeywordMonitor':
        return monitor.keyWordMonitor?.host
      case 'CronJobMonitor':
        return monitor.cronJobMonitor?.host
      default:
        return 'N/A'
    }
  }

  const getSuccessRate = () => {
    if (!monitor.logs || monitor.logs.length === 0) return 0
    const upLogs = monitor.logs.filter((log) => log.status === 'up').length
    return Math.round((upLogs / monitor.logs.length) * 100)
  }

  const successRate = getSuccessRate()
  const host = getHost()

  return (
    <Card
      component={motion.div}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      sx={{
        height: '100%',
        bgcolor: alpha('#fff', 0.03),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha('#10b981', 0.15)}`,
        borderRadius: 2.5,
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          borderColor: alpha('#10b981', 0.4),
          boxShadow: `0 12px 40px ${alpha('#10b981', 0.15)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, #10b981, #34d399)`,
          opacity: monitor.status === 'up' ? 1 : 0.3,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2.5, pb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.05rem',
                fontWeight: 700,
                color: '#fff',
                mb: 1.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {monitor.name}
            </Typography>
            <StatusIndicator status={monitor.status} />
          </Box>

          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              color: alpha('#fff', 0.5),
              transition: 'all 0.2s',
              '&:hover': {
                color: '#10b981',
                bgcolor: alpha('#10b981', 0.1),
              },
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Stack>

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              bgcolor: alpha('#0f172a', 0.98),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha('#10b981', 0.2)}`,
              borderRadius: 2,
              minWidth: 180,
              mt: 0.5,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              navigate(`/user/monitors/${monitor.id}/detail`)
              setAnchorEl(null)
            }}
            sx={{
              fontSize: '0.875rem',
              color: '#fff',
              py: 1.25,
              '&:hover': {
                bgcolor: alpha('#10b981', 0.15),
              },
            }}
          >
            <Visibility sx={{ mr: 1.5, fontSize: 18, color: '#10b981' }} />
            Detaylar
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigate(`/user/monitors/${monitor.id}/monitor`)
              setAnchorEl(null)
            }}
            sx={{
              fontSize: '0.875rem',
              color: '#fff',
              py: 1.25,
              '&:hover': {
                bgcolor: alpha('#10b981', 0.15),
              },
            }}
          >
            <Edit sx={{ mr: 1.5, fontSize: 18, color: '#10b981' }} />
            Düzenle
          </MenuItem>
          <MenuItem
            onClick={() => {
              onToggle(monitor)
              setAnchorEl(null)
            }}
            sx={{
              fontSize: '0.875rem',
              color: '#fff',
              py: 1.25,
              '&:hover': {
                bgcolor: alpha('#10b981', 0.15),
              },
            }}
          >
            {monitor.isActiveByOwner ? (
              <>
                <Pause sx={{ mr: 1.5, fontSize: 18, color: '#10b981' }} />
                Duraklat
              </>
            ) : (
              <>
                <PlayArrow sx={{ mr: 1.5, fontSize: 18, color: '#10b981' }} />
                Başlat
              </>
            )}
          </MenuItem>
          <Box sx={{ my: 0.5, mx: 1.5, borderTop: `1px solid ${alpha('#10b981', 0.15)}` }} />
          <MenuItem
            onClick={() => {
              onDelete(monitor)
              setAnchorEl(null)
            }}
            sx={{
              fontSize: '0.875rem',
              color: '#ef4444',
              py: 1.25,
              '&:hover': {
                bgcolor: alpha('#ef4444', 0.15),
              },
            }}
          >
            <Delete sx={{ mr: 1.5, fontSize: 18 }} />
            Sil
          </MenuItem>
        </Menu>
      </Box>

      {/* Type & Host */}
      <Box sx={{ px: 2.5, pb: 2 }}>
        <Box
          sx={{
            p: 1.75,
            bgcolor: alpha('#000', 0.3),
            borderRadius: 1.5,
            border: `1px solid ${alpha('#10b981', 0.1)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
            <Circle sx={{ fontSize: 6, color: '#10b981' }} />
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: alpha('#fff', 0.5),
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {monitor.monitorType}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              color: '#10b981',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {host}
          </Typography>
        </Box>
      </Box>

      {/* Metrics */}
      <Box sx={{ px: 2.5, pb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: alpha('#fff', 0.5),
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Uptime
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  color: successRate >= 95 ? '#10b981' : successRate >= 85 ? '#f59e0b' : '#ef4444',
                  lineHeight: 1,
                }}
              >
                {successRate}%
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: alpha('#fff', 0.5),
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Kontrol
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  color: alpha('#fff', 0.8),
                  lineHeight: 1,
                }}
              >
                {monitor.logs?.length || 0}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Success Rate Bar */}
      <Box sx={{ px: 2.5, pb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={successRate}
          sx={{
            height: 6,
            borderRadius: 1,
            bgcolor: alpha('#10b981', 0.1),
            '& .MuiLinearProgress-bar': {
              bgcolor: successRate >= 95 ? '#10b981' : successRate >= 85 ? '#f59e0b' : '#ef4444',
              borderRadius: 1,
            },
          }}
        />
      </Box>

      {/* Activity Chart */}
      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color: alpha('#fff', 0.5),
            mb: 1,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          Son 24 Saat
        </Typography>
        <Stack direction="row" spacing={0.4} sx={{ height: 36 }}>
          {monitor.logs?.slice(-24).map((log, idx) => (
            <Tooltip
              key={idx}
              title={`${log.status === 'up' ? 'Başarılı' : 'Hatalı'} - ${log.responseTime}ms`}
              arrow
            >
              <Box
                sx={{
                  flex: 1,
                  bgcolor:
                    log.status === 'up'
                      ? '#10b981'
                      : log.status === 'down'
                      ? '#ef4444'
                      : '#f59e0b',
                  borderRadius: 0.75,
                  opacity: 0.85,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scaleY(1.15)',
                  },
                }}
              />
            </Tooltip>
          ))}
        </Stack>
      </Box>
    </Card>
  )
}

// Stats Card
const StatsCard = ({ title, value, icon: Icon, color, trend, trendUp, loading }) => {
  return (
    <Card
      component={motion.div}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      sx={{
        bgcolor: alpha('#fff', 0.03),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(color, 0.2)}`,
        borderRadius: 2.5,
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          borderColor: alpha(color, 0.4),
          boxShadow: `0 8px 32px ${alpha(color, 0.15)}`,
        },
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              bgcolor: alpha(color, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 24, color }} />
          </Box>
          {trend && (
            <Chip
              icon={trendUp ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
              label={trend}
              size="small"
              sx={{
                height: 24,
                bgcolor: alpha(trendUp ? '#10b981' : '#ef4444', 0.15),
                color: trendUp ? '#10b981' : '#ef4444',
                fontSize: '0.7rem',
                fontWeight: 700,
                border: `1px solid ${alpha(trendUp ? '#10b981' : '#ef4444', 0.3)}`,
                '& .MuiChip-icon': {
                  color: trendUp ? '#10b981' : '#ef4444',
                },
              }}
            />
          )}
        </Stack>

        {loading ? (
          <Skeleton width={60} height={36} sx={{ bgcolor: alpha('#fff', 0.05) }} />
        ) : (
          <Typography
            sx={{
              fontSize: '2.25rem',
              fontWeight: 700,
              color,
              lineHeight: 1,
              mb: 0.75,
            }}
          >
            {value}
          </Typography>
        )}

        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: alpha('#fff', 0.6),
          }}
        >
          {title}
        </Typography>
      </Box>
    </Card>
  )
}

// Main Dashboard
export default function MonitoringDashboard() {
  const [monitors, setMonitors] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  // Fetch monitors
  useEffect(() => {
    const fetchMonitors = async () => {
      try {
        const response = await api.get('monitors/')
        if (response.data) {
          setMonitors(response.data)
          setLoading(false)
        }
      } catch (error) {
        console.error('Error fetching monitors:', error)
        setLoading(false)
      }
    }

    fetchMonitors()
    const interval = setInterval(fetchMonitors, 30000)
    return () => clearInterval(interval)
  }, [])

  // Filter monitors
  const filteredMonitors = monitors.filter((monitor) => {
    const matchesSearch =
      monitor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      Object.values(monitor).some(
        (val) =>
          typeof val === 'object' && val?.host?.toLowerCase().includes(searchQuery.toLowerCase())
      )

    const matchesStatus = statusFilter === 'all' || monitor.status === statusFilter

    return matchesSearch && matchesStatus
  })

  // Stats
  const stats = [
    {
      title: 'Toplam İzleme',
      value: monitors.length,
      icon: Circle,
      color: '#10b981',
      trend: '+12%',
      trendUp: true,
    },
    {
      title: 'Aktif',
      value: monitors.filter((m) => m.status === 'up').length,
      icon: CheckCircle,
      color: '#10b981',
      trend: '+5%',
      trendUp: true,
    },
    {
      title: 'Hata',
      value: monitors.filter((m) => m.status === 'down').length,
      icon: Error,
      color: '#ef4444',
      trend: '-2%',
      trendUp: false,
    },
    {
      title: 'Durduruldu',
      value: monitors.filter((m) => !m.isActiveByOwner).length,
      icon: Pause,
      color: '#64748b',
      trend: '0%',
      trendUp: true,
    },
  ]

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#fff',
                mb: 0.5,
              }}
            >
              Monitoring Dashboard
            </Typography>
            <Typography
              sx={{
                fontSize: '0.9rem',
                color: alpha('#fff', 0.6),
              }}
            >
              Sistemlerinizi gerçek zamanlı izleyin
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/user/monitors/new')}
            sx={{
              bgcolor: '#10b981',
              color: '#000',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.9375rem',
              boxShadow: `0 4px 20px ${alpha('#10b981', 0.3)}`,
              '&:hover': {
                bgcolor: '#059669',
                boxShadow: `0 6px 24px ${alpha('#10b981', 0.4)}`,
              },
            }}
          >
            Yeni İzleme Ekle
          </Button>
        </Stack>

        {/* Stats */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
              >
                <StatsCard {...stat} loading={loading} />
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Search & Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            placeholder="Monitor adı veya host ile ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: alpha('#10b981', 0.6) }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: alpha('#fff', 0.03),
                borderRadius: 2,
                color: '#fff',
                '& fieldset': {
                  borderColor: alpha('#10b981', 0.2),
                },
                '&:hover fieldset': {
                  borderColor: alpha('#10b981', 0.3),
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#10b981',
                },
              },
            }}
          />

          <Stack direction="row" spacing={1}>
            {[
              { value: 'all', label: 'Tümü' },
              { value: 'up', label: 'Aktif' },
              { value: 'down', label: 'Hatalı' },
              { value: 'paused', label: 'Durduruldu' },
            ].map((filter) => (
              <Button
                key={filter.value}
                variant={statusFilter === filter.value ? 'contained' : 'outlined'}
                onClick={() => setStatusFilter(filter.value)}
                sx={{
                  minWidth: 100,
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  ...(statusFilter === filter.value
                    ? {
                        bgcolor: '#10b981',
                        color: '#000',
                        boxShadow: `0 4px 12px ${alpha('#10b981', 0.3)}`,
                        '&:hover': {
                          bgcolor: '#059669',
                        },
                      }
                    : {
                        borderColor: alpha('#10b981', 0.3),
                        color: alpha('#fff', 0.7),
                        '&:hover': {
                          borderColor: alpha('#10b981', 0.5),
                          bgcolor: alpha('#10b981', 0.05),
                        },
                      }),
                }}
              >
                {filter.label}
              </Button>
            ))}

            <IconButton
              onClick={() => window.location.reload()}
              sx={{
                border: `1px solid ${alpha('#10b981', 0.3)}`,
                borderRadius: 2,
                color: alpha('#10b981', 0.7),
                '&:hover': {
                  borderColor: alpha('#10b981', 0.5),
                  bgcolor: alpha('#10b981', 0.05),
                  color: '#10b981',
                },
              }}
            >
              <Refresh />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Monitor Cards */}
      {loading ? (
        <Grid container spacing={2.5}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton
                variant="rectangular"
                height={340}
                sx={{
                  borderRadius: 2.5,
                  bgcolor: alpha('#fff', 0.03),
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : filteredMonitors.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            bgcolor: alpha('#fff', 0.03),
            borderRadius: 2.5,
            border: `1px solid ${alpha('#10b981', 0.15)}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: alpha('#fff', 0.6),
              mb: 1,
              fontWeight: 600,
            }}
          >
            İzleme Bulunamadı
          </Typography>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: alpha('#fff', 0.4),
            }}
          >
            {searchQuery || statusFilter !== 'all'
              ? 'Filtreleri değiştirin veya yeni bir izleme ekleyin'
              : 'Henüz izleme eklenmemiş'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filteredMonitors.map((monitor, index) => (
            <Grid item xs={12} sm={6} md={4} key={monitor.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
              >
                <MonitorCard
                  monitor={monitor}
                  onView={(m) => navigate(`/user/monitors/${m.id}/detail`)}
                  onEdit={(m) => navigate(`/user/monitors/${m.id}/monitor`)}
                  onDelete={(m) => console.log('Delete', m)}
                  onToggle={(m) => console.log('Toggle', m)}
                />
              </motion.div>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  )
}